// One Bluetooth search for every kind of device: what the chooser lists, which slot a picked device fills,
// what a failed request means, and which devices to bring back at the next launch. No browser state but the stored list.
import { UUID } from './gatt.js';

export type DeviceKind = 'trainer' | 'power' | 'csc' | 'hr' | 'ride';

export const ZW_SVC = '00000001-19ca-4651-86e5-fa29dcdd09d1';
export const ZW_SVC16 = '0000fc82-0000-1000-8000-00805f9b34fb';

const HR = 'heart_rate';
/** Priority order: a smart trainer also exposes power and speed, a Zwift Hub carries the Zwift name but speaks FTMS. */
const BY_SERVICE: readonly (readonly [number | string, DeviceKind])[] = [[UUID.ftms, 'trainer'], [ZW_SVC16, 'ride'], [ZW_SVC, 'ride'], [UUID.cps, 'power'], [UUID.csc, 'csc'], [HR, 'hr']];

/** Services probed on a picked device, in priority order. */
export const SCAN_SERVICES = BY_SERVICE.map(([s]) => s);
/** requestDevice options: the chooser lists all supported devices at once. */
export const SCAN_OPTIONS = {
  filters: [...SCAN_SERVICES.map(s => ({ services: [s] })), { namePrefix: 'Zwift' }],
  optionalServices: [...SCAN_SERVICES, 'battery_service', 'device_information'],
};

/** Slot of a device from the services it really exposes; null when none is supported. */
export function classify(services: readonly (number | string)[]): DeviceKind | null {
  return BY_SERVICE.find(([s]) => services.includes(s))?.[1] ?? null;
}

export type RequestFailure = 'cancelled' | 'blocked' | 'unavailable' | 'other';
/**
 * Chrome rejects with NotFoundError when the chooser is closed, when the adapter is off and when the browser itself
 * refuses the API ("Web Bluetooth API globally disabled": Brave, Opera and Vivaldi ship it off, an enterprise policy
 * can turn it off too). The last one is told apart: no system setting fixes it, only the browser's own.
 */
export function requestFailure(e: { name?: string; message?: string } | null | undefined): RequestFailure {
  if (e?.name !== 'NotFoundError') return 'other';
  const message = e.message ?? '';
  return /cancel/i.test(message) ? 'cancelled' : /disabled|blocked/i.test(message) ? 'blocked' : 'unavailable';
}

const KEY = 'gruvetto.bluetooth';
const KINDS: readonly string[] = ['trainer', 'power', 'csc', 'hr', 'ride'];
type Known = Record<string, DeviceKind>;

/** Paired devices by browser-assigned id. A device setting: kept on this machine, like the rest of them. */
export function knownDevices(): Known {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(KEY) || '{}');
    if (!raw || typeof raw !== 'object') return {};
    return Object.fromEntries(Object.entries(raw).filter(([, k]) => KINDS.includes(k as string))) as Known;
  } catch { return {}; }
}
const save = (k: Known) => { try { localStorage.setItem(KEY, JSON.stringify(k)); } catch { /* quota or private mode */ } };
export function rememberDevice(id: string, kind: DeviceKind) { save({ ...knownDevices(), [id]: kind }); }
/** Forgets a slot: an intentional disconnect must not come back by itself at the next launch. */
export function forgetKind(kind: DeviceKind) { save(Object.fromEntries(Object.entries(knownDevices()).filter(([, k]) => k !== kind))); }
